
import React from "react";
import { RibaStage, Project } from "@/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import StageInputs from "./StageInputs";
import StageTasks from "./StageTasks";
import StageOutputs from "./StageOutputs";
import StageApprovals from "./StageApprovals";
import StageDocuments from "./StageDocuments";
import StageRisks from "./StageRisks";
import StageInformationExchange from "./StageInformationExchange";
import { useIsMobile } from "@/hooks/use-mobile";
import { ScrollArea } from "@/components/ui/scroll-area";

interface StageWorkflowProps {
  project: Project;
  stage: RibaStage;
  onUpdate?: () => void;
}

export default function StageWorkflow({ project, stage, onUpdate }: StageWorkflowProps) {
  const isMobile = useIsMobile();
  
  return (
    <div className="space-y-6">
      <Tabs defaultValue="summary" className="w-full">
        <ScrollArea className="w-full">
          <TabsList className={`${isMobile ? 'grid-cols-4' : 'grid-cols-7'} mb-4 w-full inline-flex`}>
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="info-exchange">{isMobile ? "Info" : "Info Exchange"}</TabsTrigger>
            <TabsTrigger value="inputs">Inputs</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            {!isMobile && (
              <>
                <TabsTrigger value="outputs">Outputs</TabsTrigger>
                <TabsTrigger value="documents">Docs</TabsTrigger>
                <TabsTrigger value="approvals">Approvals</TabsTrigger>
              </>
            )}
            {isMobile && (
              <TabsTrigger value="more">More</TabsTrigger>
            )}
          </TabsList>
        </ScrollArea>
        
        <TabsContent value="summary">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl truncate">Stage {stage.id}: {stage.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm">{stage.description}</p>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                  <dd className="mt-1 text-sm">{stage.status}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Progress</dt>
                  <dd className="mt-1 text-sm">{stage.progress}%</dd>
                </div>
                {stage.dateStarted && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Date Started</dt>
                    <dd className="mt-1 text-sm">{stage.dateStarted.toLocaleDateString()}</dd>
                  </div>
                )}
                {stage.dateCompleted && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Date Completed</dt>
                    <dd className="mt-1 text-sm">{stage.dateCompleted.toLocaleDateString()}</dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="info-exchange">
          <StageInformationExchange project={project} stage={stage} onUpdate={onUpdate} />
        </TabsContent>
        
        <TabsContent value="inputs">
          <StageInputs project={project} stage={stage} onUpdate={onUpdate} />
        </TabsContent>
        
        <TabsContent value="tasks">
          <StageTasks project={project} stage={stage} onUpdate={onUpdate} />
        </TabsContent>
        
        <TabsContent value="outputs">
          <StageOutputs project={project} stage={stage} onUpdate={onUpdate} />
        </TabsContent>
        
        <TabsContent value="documents">
          <StageDocuments project={project} stage={stage} onUpdate={onUpdate} />
        </TabsContent>
        
        <TabsContent value="approvals">
          <StageApprovals project={project} stage={stage} onUpdate={onUpdate} />
        </TabsContent>
        
        {isMobile && (
          <TabsContent value="more">
            <Tabs defaultValue="outputs" className="w-full">
              <TabsList className="grid grid-cols-3 mb-4">
                <TabsTrigger value="outputs">Outputs</TabsTrigger>
                <TabsTrigger value="documents">Docs</TabsTrigger>
                <TabsTrigger value="approvals">Approvals</TabsTrigger>
              </TabsList>
              
              <TabsContent value="outputs">
                <StageOutputs project={project} stage={stage} onUpdate={onUpdate} />
              </TabsContent>
              
              <TabsContent value="documents">
                <StageDocuments project={project} stage={stage} onUpdate={onUpdate} />
              </TabsContent>
              
              <TabsContent value="approvals">
                <StageApprovals project={project} stage={stage} onUpdate={onUpdate} />
              </TabsContent>
            </Tabs>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
